"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FaTelegram, FaInstagram } from "react-icons/fa";
import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  Navigation,
  MapPin,
  List,
  ExternalLink
} from "lucide-react";

interface HelpProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function Help({ isOpen, onOpenChange }: HelpProps) {
  const [showGuide, setShowGuide] = useState(false);
  const router = useRouter();

  const handleViewFullHelp = () => {
    onOpenChange(false);
    router.push('/help');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-4 rounded-2xl max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle className="text-center">Need Help?</DialogTitle>
        </DialogHeader>
        {!showGuide ? (
          <>
            <Button
              variant="default"
              className="w-full font-bold shadow-md shadow-primary/80"
              onClick={() => setShowGuide(true)}
            >
              Quick Guide: How to Use the Map
            </Button>
            <Button 
              variant="outline" 
              className="w-full shadow-md flex items-center justify-center gap-2 font-bold"
              onClick={() => window.open('https://t.me/batchfinderdev', '_blank')}
            >
              <FaTelegram className="w-8 h-8 text-[#229ED9]" />
              @BATCHFINDERDEV
              <FaTelegram className="w-8 h-8 text-[#229ED9]" />
            </Button>
            <Button 
              variant="outline"
              className="w-full shadow-md flex items-center justify-center gap-2 font-bold"
              onClick={() => window.open('https://t.me/+IOe5ap0cjYo1OTUx', '_blank')}
            >
              <FaTelegram className="w-8 h-8 text-[#229ED9]" />
              TELEGRAM GROUP
              <FaTelegram className="w-8 h-8 text-[#229ED9]" />
            </Button>
            <Button 
              variant="outline"
              className="w-full shadow-md flex items-center justify-center gap-2 font-bold"
              onClick={() => window.open('https://instagram.com/batchfinder', '_blank')}
            >
              <FaInstagram className="w-8 h-8 text-[#E4405F]" />
              FOLLOW US ON INSTAGRAM
              <FaInstagram className="w-8 h-8 text-[#E4405F]" />
            </Button>
            <Button 
              variant="outline"
              className="w-full shadow-md flex items-center justify-center gap-2 font-bold"
              onClick={handleViewFullHelp}
            >
              <ExternalLink className="w-4 h-4" />
              View Full Help Page
            </Button>
            <Button 
              variant="default"
              className="w-full mt-2 font-bold"
              onClick={() => onOpenChange(false)}
            >
              CLOSE
            </Button>
          </>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="text-left space-y-2">
              <h2 className="text-lg font-bold mb-2">Map Legend & Quick Guide</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="bg-background/80 backdrop-blur-sm rounded-full p-1.5 shadow-lg hover:bg-background/90 text-foreground border border-gray-200 w-8 h-8 flex items-center justify-center">
                    <Navigation size={24} className="text-blue-500" />
                  </span>
                  <span className="text-sm text-muted-foreground">Brings you to your real, device-detected location.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-background/80 backdrop-blur-sm rounded-full p-1.5 shadow-lg hover:bg-background/90 text-foreground border border-gray-200 w-8 h-8 flex items-center justify-center">
                    <MapPin size={24} className="text-primary" />
                  </span>
                  <span className="text-sm text-muted-foreground">Brings you to your currently set location (fake or real, whichever Batchfinder is using).</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-background/80 backdrop-blur-sm rounded-full p-1.5 shadow-lg hover:bg-background/90 text-foreground border border-gray-200 w-8 h-8 flex items-center justify-center">
                    <List size={24} className="text-gray-700" />
                  </span>
                  <span className="text-sm text-muted-foreground">Opens a modal to browse and view all stores, see demand, and locate them on the map. Tap a store and use "Show in Map" to highlight it.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-background/80 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-background/90 text-foreground border border-gray-200 w-8 h-8 flex items-center justify-center">
                    <span className="text-2xl" role="img" aria-label="Store Marker">🛒</span>
                  </span>
                  <span className="text-sm text-muted-foreground">Tap on stores to see demand levels and distance from your current location.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-background/80 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-background/90 text-foreground border-2 border-red-500 w-8 h-8 flex items-center justify-center">
                    <span className="text-2xl" role="img" aria-label="High Demand Store">🛒</span>
                  </span>
                  <span className="text-sm text-muted-foreground">Stores highlighted in red or orange are in higher demand.</span>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                <b>Tip:</b> If you are currently using a fake location, you can start using your real location by pressing <b>"Use current location"</b> above your current location marker.
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full font-bold"
              onClick={() => setShowGuide(false)}
            >
              Back
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
} 